/**
 * CSV Import Service (Enhanced)
 *
 * Handles importing transactions from 1Money CSV export.
 * Features:
 * - Parses account balances from CSV footer (source of truth)
 * - Detects and flags "error fix" transactions
 * - Detects recurring transaction patterns
 * - Maps short account names to full names (C=Chase, Rev=Revolut)
 * - Properly handles transfers between accounts
 */

import Papa from 'papaparse';
import { db, Transaction, Category, Account } from './db';
import { transactionService, TransactionInput } from './transactions';
import { accountService } from './accounts';
import { generateId } from '../utils/id';
import { devlog } from './devlog';
import { dataEvents } from './dataEvents';
import { syncManager } from './syncManager';

// ============================================================
// Types
// ============================================================

interface OneMoneyCsvRow {
  DATE: string;
  TYPE: string;
  'FROM ACCOUNT': string;
  'TO ACCOUNT/TO CATEGORY': string;
  AMOUNT: string;
  CURRENCY: string;
  NOTES: string;
  TAGS?: string;
}

export interface ImportResult {
  success: boolean;
  imported: number;
  skipped: number;
  errorFixCount: number;
  errorFixTotal: number;
  recurringDetected: number;
  errors: string[];
  warnings: string[];
  newAccounts: string[];
  newCategories: string[];
  footerBalances: Map<string, number>;
  balancesApplied: boolean;
}

export interface ImportProgress {
  current: number;
  total: number;
  status: string;
}

export interface ImportOptions {
  useFooterBalances?: boolean;  // Use balances from CSV footer (recommended)
  skipErrorFix?: boolean;       // Skip "error fix" type transactions
  detectRecurring?: boolean;    // Tag recurring transactions
}

// ============================================================
// Account Name Mapping (Short -> Full)
// ============================================================

const ACCOUNT_NAME_MAP: Record<string, { fullName: string; type: Account['type'] }> = {
  'C': { fullName: 'Chase', type: 'checking' },
  'C ': { fullName: 'Chase', type: 'checking' },
  'Rev': { fullName: 'Revolut', type: 'checking' },
  's Rev s': { fullName: 'Revolut Savings', type: 'savings' },
  'C-app': { fullName: 'Cash App', type: 'checking' },
  's C app s': { fullName: 'Cash App Savings', type: 'savings' },
  'paypal': { fullName: 'PayPal', type: 'checking' },
  'paypal ': { fullName: 'PayPal', type: 'checking' },
  'crypto com': { fullName: 'Crypto.com', type: 'checking' },
  'PNL -21500': { fullName: 'P&L Tracking', type: 'checking' },
  'PNL REDACTED': { fullName: 'P&L Tracking', type: 'checking' },
  'pending': { fullName: 'Pending', type: 'checking' },
  'pending ': { fullName: 'Pending', type: 'checking' },
  'Cb wlt': { fullName: 'Coinbase Wallet', type: 'checking' },
  'Kraken pro': { fullName: 'Kraken Pro', type: 'checking' },
  'baht': { fullName: 'Thai Baht Account', type: 'checking' },
  'misc C': { fullName: 'Misc Cash', type: 'cash' },
  'Sfarm phone lawn etc': { fullName: 'State Farm/Bills', type: 'checking' },
};

// ============================================================
// Recurring Transaction Patterns
// ============================================================

interface RecurringPattern {
  pattern: RegExp;
  tag: string;
  category?: string;
}

const RECURRING_PATTERNS: RecurringPattern[] = [
  // Bills & Utilities
  { pattern: /xfinity/i, tag: 'recurring:internet', category: 'electric misc bills etc' },
  { pattern: /\bdte\b|electric bill/i, tag: 'recurring:electric', category: 'electric misc bills etc' },
  { pattern: /consumers|house gas|gas bill/i, tag: 'recurring:gas', category: 'electric misc bills etc' },
  { pattern: /google.*phone|phone.*google|\bphone\b/i, tag: 'recurring:phone', category: 'electric misc bills etc' },
  { pattern: /amazon prime/i, tag: 'recurring:prime', category: 'electric misc bills etc' },

  // House
  { pattern: /house payment|mortgage|buy.*note.*house/i, tag: 'recurring:mortgage', category: 'House' },

  // Software/Subscriptions
  { pattern: /obsidian/i, tag: 'recurring:software' },
  { pattern: /twitch turbo|twitch/i, tag: 'recurring:streaming' },

  // Car
  { pattern: /malibu.*payment|car.*payment/i, tag: 'recurring:car-payment', category: 'Car' },
  { pattern: /state farm|car.*insurance/i, tag: 'recurring:car-insurance', category: 'Car' },

  // Entertainment/Streaming
  { pattern: /spotify/i, tag: 'recurring:spotify', category: 'Entertainment' },
  { pattern: /netflix/i, tag: 'recurring:netflix', category: 'Entertainment' },
  { pattern: /hulu/i, tag: 'recurring:streaming', category: 'Entertainment' },
  { pattern: /disney\+|disney plus/i, tag: 'recurring:streaming', category: 'Entertainment' },
  { pattern: /youtube premium|youtube music/i, tag: 'recurring:streaming', category: 'Entertainment' },

  // AI/Business
  { pattern: /claude|anthropic/i, tag: 'recurring:ai', category: 'Business' },
  { pattern: /openai|chatgpt|gpt/i, tag: 'recurring:ai', category: 'Business' },
  { pattern: /cursor/i, tag: 'recurring:ai', category: 'Business' },

  // Food Delivery (recurring if frequent)
  { pattern: /doordash/i, tag: 'recurring:delivery' },
  { pattern: /uber eats|ubereats/i, tag: 'recurring:delivery' },
  { pattern: /grubhub/i, tag: 'recurring:delivery' },

  // Grocery
  { pattern: /meijer/i, tag: 'food:grocery' },
];

// ============================================================
// Error Fix Detection
// ============================================================

const ERROR_FIX_PATTERNS = [
  /usd error fix/i,
  /balance fix/i,
  /error fix/i,
  /fixme/i,
  /workaround/i,
  /duplicate.*fix/i,
  /zfix/i,
  /znn.*fix/i,
];

function isErrorFixTransaction(category: string, notes: string): boolean {
  const combined = `${category} ${notes}`.toLowerCase();
  return ERROR_FIX_PATTERNS.some(p => p.test(combined));
}

// ============================================================
// Logging
// ============================================================

function log(message: string, data?: unknown): void {
  devlog.info('import', message, data);
}

function logWarn(message: string, data?: unknown): void {
  devlog.warn('import', message, data);
}

function logError(message: string, data?: unknown): void {
  devlog.error('import', message, data);
}

// ============================================================
// Main Import Function
// ============================================================

export async function importOneMoneyCSV(
  file: File,
  onProgress?: (progress: ImportProgress) => void,
  options: ImportOptions = {}
): Promise<ImportResult> {
  const {
    useFooterBalances = true,
    skipErrorFix = false,
    detectRecurring = true,
  } = options;

  const result: ImportResult = {
    success: false,
    imported: 0,
    skipped: 0,
    errorFixCount: 0,
    errorFixTotal: 0,
    recurringDetected: 0,
    errors: [],
    warnings: [],
    newAccounts: [],
    newCategories: [],
    footerBalances: new Map(),
    balancesApplied: false,
  };

  log('Starting import', { filename: file.name, size: file.size, options });

  return new Promise((resolve) => {
    Papa.parse<OneMoneyCsvRow>(file, {
      header: true,
      skipEmptyLines: false, // Need to detect footer section
      transformHeader: (header) => header.replace(/^\uFEFF/, '').trim(),
      complete: async (parseResult) => {
        try {
          await processImport(parseResult.data, result, onProgress, {
            useFooterBalances,
            skipErrorFix,
            detectRecurring,
          });
        } catch (error) {
          const errorMsg = error instanceof Error ? error.message : String(error);
          result.errors.push(`Import failed: ${errorMsg}`);
          logError('Import exception', { error: errorMsg });
        }

        result.success = result.errors.length === 0;
        resolve(result);
      },
      error: (error) => {
        result.errors.push(`Parse error: ${error.message}`);
        logError('CSV parse error', { error: error.message });
        resolve(result);
      },
    });
  });
}

async function processImport(
  rows: OneMoneyCsvRow[],
  result: ImportResult,
  onProgress?: (progress: ImportProgress) => void,
  options?: ImportOptions
): Promise<void> {
  // Step 1: Separate transaction rows from footer balance rows
  const { transactionRows, footerBalances } = separateFooterBalances(rows);
  result.footerBalances = footerBalances;

  log('Separated data', {
    transactionRows: transactionRows.length,
    footerAccounts: footerBalances.size,
  });

  if (transactionRows.length === 0) {
    result.errors.push('No transaction data found in CSV');
    return;
  }

  // Step 2: Validate headers
  const requiredHeaders = ['DATE', 'TYPE', 'FROM ACCOUNT', 'TO ACCOUNT/TO CATEGORY', 'AMOUNT'];
  const headers = Object.keys(transactionRows[0] || {});
  const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));

  if (missingHeaders.length > 0) {
    result.errors.push(`Missing required columns: ${missingHeaders.join(', ')}`);
    return;
  }

  // Step 3: Process each row
  const transactions: TransactionInput[] = [];
  const total = transactionRows.length;

  for (let i = 0; i < total; i++) {
    const row = transactionRows[i];

    // Update progress every 100 rows
    if (onProgress && i % 100 === 0) {
      onProgress({
        current: i + 1,
        total,
        status: `Processing row ${i + 1} of ${total}`,
      });
    }

    // Skip rows without essential data
    if (!row.DATE || !row.TYPE || !row.AMOUNT) {
      result.skipped++;
      continue;
    }

    // Check for error fix transactions
    const category = row['TO ACCOUNT/TO CATEGORY'] || '';
    const notes = row.NOTES || '';

    if (isErrorFixTransaction(category, notes)) {
      result.errorFixCount++;
      result.errorFixTotal += Math.abs(parseFloat(row.AMOUNT) || 0);

      if (options?.skipErrorFix) {
        logWarn('Skipping error fix transaction', { row: i + 1, amount: row.AMOUNT, notes });
        result.skipped++;
        continue;
      } else {
        logWarn('Error fix transaction detected', { row: i + 1, amount: row.AMOUNT, notes });
      }
    }

    try {
      const transaction = await parseOneMoneyRow(row, result, options?.detectRecurring);
      if (transaction) {
        transactions.push(transaction);
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      result.errors.push(`Row ${i + 1}: ${errorMsg}`);
      result.skipped++;
    }
  }

  // Step 4: Bulk insert transactions
  if (transactions.length > 0) {
    try {
      await transactionService.bulkCreate(transactions);
      result.imported = transactions.length;
      log('Transactions inserted', { count: result.imported });
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      result.errors.push(`Bulk insert failed: ${errorMsg}`);
      logError('Bulk insert failed', { error: errorMsg });
    }
  }

  // Step 5: Apply footer balances OR recalculate
  if (options?.useFooterBalances && footerBalances.size > 0) {
    await applyFooterBalances(footerBalances, result);
    result.balancesApplied = true;
    log('Applied footer balances', { accounts: footerBalances.size });
  } else {
    await accountService.recalculateAllBalances();
    log('Recalculated balances from transactions');
  }

  // BUG-020 FIX: Emit dataEvents for newly created categories
  // This notifies other pages (CategoriesPage, QuickAddModal) about new categories
  if (result.newCategories.length > 0) {
    dataEvents.emit('categories');
    log(`Emitted categories event for ${result.newCategories.length} new categories`);
  }

  // Also emit for new accounts (already handled by bulkCreate, but explicit here for clarity)
  if (result.newAccounts.length > 0) {
    dataEvents.emit('accounts');
    log(`Emitted accounts event for ${result.newAccounts.length} new accounts`);
  }

  log('Import complete', {
    imported: result.imported,
    skipped: result.skipped,
    errorFixCount: result.errorFixCount,
    errorFixTotal: result.errorFixTotal,
    recurringDetected: result.recurringDetected,
    newAccounts: result.newAccounts.length,
    newCategories: result.newCategories.length,
    balancesApplied: result.balancesApplied,
  });
}

// ============================================================
// Footer Balance Parsing
// ============================================================

interface SeparatedData {
  transactionRows: OneMoneyCsvRow[];
  footerBalances: Map<string, number>;
}

function separateFooterBalances(rows: OneMoneyCsvRow[]): SeparatedData {
  const transactionRows: OneMoneyCsvRow[] = [];
  const footerBalances = new Map<string, number>();

  let inFooterSection = false;

  for (const row of rows) {
    // Detect start of footer section (empty row followed by NAME,BALANCE header)
    if (!row.DATE && !row.TYPE && !row.AMOUNT) {
      // Check if next-ish content looks like balance section
      // The footer starts with empty rows, then NAME,BALANCE,CURRENCY headers
      inFooterSection = true;
      continue;
    }

    if (inFooterSection) {
      // Footer row format: NAME in DATE column, BALANCE in TYPE column
      // Or it could be: NAME field, BALANCE field, CURRENCY field
      const rowAny = row as unknown as Record<string, string>;
      const nameField = row.DATE || rowAny['NAME'] || '';
      const balanceField = row.TYPE || rowAny['BALANCE'] || '';

      if (nameField && balanceField) {
        const balance = parseFloat(balanceField);
        if (!isNaN(balance)) {
          const accountName = nameField.trim();
          footerBalances.set(accountName, balance);
        }
      }
    } else {
      transactionRows.push(row);
    }
  }

  return { transactionRows, footerBalances };
}

async function applyFooterBalances(
  footerBalances: Map<string, number>,
  _result: ImportResult // Reserved for future use (could track which balances applied)
): Promise<void> {
  for (const [name, balance] of footerBalances) {
    // Find account by name (try both trimmed and with spaces)
    let account = await accountService.getByName(name);
    if (!account) {
      account = await accountService.getByName(name.trim());
    }

    if (account) {
      // BUG-058 FIX: Calculate initial balance (balance not derived from transactions)
      // Formula: initialBalance = footerBalance - sum(transactions)
      // This preserves the starting balance during reconciliation
      const calculatedBalance = await accountService.calculateBalance(account.id!);
      // Note: calculatedBalance already includes old initialBalance, so we need to subtract it
      const oldInitialBalance = account.initialBalance ?? 0;
      const transactionSum = calculatedBalance - oldInitialBalance;
      const newInitialBalance = balance - transactionSum;

      await db.accounts.update(account.id!, {
        balance,
        initialBalance: newInitialBalance,
        updatedAt: new Date(),
        syncStatus: 'pending',
      });
      log('Applied balance from footer', { account: name, balance, initialBalance: newInitialBalance });
    } else {
      // Account doesn't exist - might be an inactive one
      logWarn('Footer balance for unknown account', { account: name, balance });
    }
  }
}

// ============================================================
// Row Parsing
// ============================================================

async function parseOneMoneyRow(
  row: OneMoneyCsvRow,
  result: ImportResult,
  detectRecurring?: boolean
): Promise<TransactionInput | null> {
  // Parse date
  const date = parseDate(row.DATE);
  if (!date) {
    throw new Error(`Invalid date format: ${row.DATE}`);
  }

  // Parse amount
  const amount = Math.abs(parseFloat(row.AMOUNT));
  if (isNaN(amount)) {
    throw new Error(`Invalid amount: ${row.AMOUNT}`);
  }

  // Get or create source account
  const accountName = normalizeAccountName(row['FROM ACCOUNT']);
  if (!accountName) {
    throw new Error('Missing FROM ACCOUNT');
  }

  let account = await accountService.getByName(accountName);
  if (!account) {
    const mapping = ACCOUNT_NAME_MAP[accountName] || ACCOUNT_NAME_MAP[row['FROM ACCOUNT']];
    await accountService.create({
      name: accountName,
      fullName: mapping?.fullName,
      type: mapping?.type || 'checking',
      currency: row.CURRENCY || 'USD',
      isActive: true,
    });
    result.newAccounts.push(accountName);
    account = await accountService.getByName(accountName);
    log('Created new account', { name: accountName, fullName: mapping?.fullName });
  }

  // Determine transaction type
  const typeRaw = row.TYPE.toLowerCase().trim();
  let type: Transaction['type'];

  if (typeRaw === 'income' || typeRaw === 'in') {
    type = 'income';
  } else if (typeRaw === 'expense' || typeRaw === 'out') {
    type = 'expense';
  } else if (typeRaw === 'transfer' || typeRaw === 'trans') {
    type = 'transfer';
  } else {
    throw new Error(`Unknown transaction type: ${row.TYPE}`);
  }

  // Detect recurring patterns
  const tags: string[] = [];
  if (detectRecurring) {
    const notes = row.NOTES || '';
    for (const pattern of RECURRING_PATTERNS) {
      if (pattern.pattern.test(notes)) {
        tags.push(pattern.tag);
        result.recurringDetected++;
      }
    }
  }

  // Build transaction
  const transaction: TransactionInput = {
    accountId: account!.id!,
    type,
    amount,
    description: row.NOTES || '',
    date,
    currency: row.CURRENCY || 'USD',
    tags: tags.length > 0 ? tags : undefined,
  };

  // Handle category or transfer target
  const target = normalizeAccountName(row['TO ACCOUNT/TO CATEGORY']);

  if (type === 'transfer') {
    let toAccount = await accountService.getByName(target);
    if (!toAccount && target) {
      const mapping = ACCOUNT_NAME_MAP[target] || ACCOUNT_NAME_MAP[row['TO ACCOUNT/TO CATEGORY']];
      await accountService.create({
        name: target,
        fullName: mapping?.fullName,
        type: mapping?.type || 'checking',
        currency: row.CURRENCY || 'USD',
        isActive: true,
      });
      result.newAccounts.push(target);
      toAccount = await accountService.getByName(target);
      log('Created new account for transfer', { name: target, fullName: mapping?.fullName });
    }
    transaction.toAccountId = toAccount?.id;
  } else if (target) {
    let category = await getCategoryByName(target);
    if (!category) {
      const categoryId = await createCategory(target, type === 'income' ? 'income' : 'expense');
      result.newCategories.push(target);
      category = await db.categories.get(categoryId);
      log('Created new category', { name: target, type });
    }
    transaction.categoryId = category?.id;
  }

  return transaction;
}

// ============================================================
// Helper Functions
// ============================================================

function normalizeAccountName(name: string): string {
  if (!name) return '';
  // Trim trailing/leading spaces but preserve internal structure
  return name.trim();
}

function parseDate(dateStr: string): Date | null {
  if (!dateStr) return null;

  const parts = dateStr.split('/');
  if (parts.length !== 3) return null;

  const month = parseInt(parts[0], 10) - 1;
  const day = parseInt(parts[1], 10);
  let year = parseInt(parts[2], 10);

  if (year < 100) {
    year += year > 50 ? 1900 : 2000;
  }

  const date = new Date(year, month, day);

  if (
    date.getMonth() !== month ||
    date.getDate() !== day ||
    date.getFullYear() !== year
  ) {
    return null;
  }

  return date;
}

async function getCategoryByName(name: string): Promise<Category | undefined> {
  return db.categories.where('name').equalsIgnoreCase(name).first();
}

async function createCategory(
  name: string,
  type: 'income' | 'expense'
): Promise<string> {
  const id = generateId();
  await db.categories.add({
    id,
    name,
    type,
    isActive: true,
  });
  return id;
}

// ============================================================
// Validation
// ============================================================

export async function validateOneMoneyCSV(file: File): Promise<{
  valid: boolean;
  rowCount: number;
  footerBalanceCount: number;
  error?: string;
}> {
  return new Promise((resolve) => {
    Papa.parse<OneMoneyCsvRow>(file, {
      header: true,
      skipEmptyLines: false,
      transformHeader: (header) => header.replace(/^\uFEFF/, '').trim(),
      complete: (result) => {
        const requiredHeaders = ['DATE', 'TYPE', 'FROM ACCOUNT', 'AMOUNT'];
        const headers = Object.keys(result.data[0] || {});
        const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));

        if (missingHeaders.length > 0) {
          resolve({
            valid: false,
            rowCount: 0,
            footerBalanceCount: 0,
            error: `Missing columns: ${missingHeaders.join(', ')}`,
          });
          return;
        }

        const { transactionRows, footerBalances } = separateFooterBalances(result.data);

        resolve({
          valid: true,
          rowCount: transactionRows.length,
          footerBalanceCount: footerBalances.size,
        });
      },
      error: (error) => {
        resolve({
          valid: false,
          rowCount: 0,
          footerBalanceCount: 0,
          error: error.message,
        });
      },
    });
  });
}

// ============================================================
// Retag Recurring Transactions
// ============================================================

/**
 * Apply recurring patterns to all existing transactions.
 * Returns count of transactions updated.
 */
export async function retagRecurringTransactions(): Promise<{
  updated: number;
  patterns: string[];
}> {
  log('Starting retag of recurring transactions');

  const allTransactions = await db.transactions.toArray();
  let updated = 0;
  const patternsMatched = new Set<string>();

  await db.transaction('rw', db.transactions, async () => {
    for (const tx of allTransactions) {
      const description = tx.description || '';
      let matched = false;
      let newTags = tx.tags ? [...tx.tags] : [];

      for (const { pattern, tag } of RECURRING_PATTERNS) {
        if (pattern.test(description)) {
          // Add tag if not already present
          if (!newTags.includes(tag)) {
            newTags.push(tag);
            matched = true;
            patternsMatched.add(tag);
          }
        }
      }

      if (matched) {
        await db.transactions.update(tx.id!, { tags: newTags, updatedAt: new Date() });
        updated++;
      }
    }
  });

  log(`Retagged ${updated} transactions with patterns: ${Array.from(patternsMatched).join(', ')}`);

  return {
    updated,
    patterns: Array.from(patternsMatched),
  };
}

/**
 * Toggle recurring tag on a specific transaction.
 */
export async function toggleRecurringTag(
  transactionId: string,
  isRecurring: boolean,
  recurringType?: string
): Promise<void> {
  const tx = await db.transactions.get(transactionId);
  if (!tx) return;

  let newTags = tx.tags ? [...tx.tags] : [];

  if (isRecurring) {
    // Add recurring tag
    const tag = recurringType ? `recurring:${recurringType}` : 'recurring:manual';
    if (!newTags.some(t => t.startsWith('recurring:'))) {
      newTags.push(tag);
    }
  } else {
    // Remove all recurring tags
    newTags = newTags.filter(t => !t.startsWith('recurring:'));
  }

  await db.transactions.update(transactionId, { tags: newTags, updatedAt: new Date() });
}

// ============================================================
// JSON Import (NinjaCash backup format)
// ============================================================

export interface JsonImportResult {
  success: boolean;
  accounts: number;
  transactions: number;
  categories: number;
  budgets: number;
  errors: string[];
}

/**
 * Import data from NinjaCash JSON backup file.
 * This restores accounts, transactions, categories, and budgets.
 */
export async function importNinjaCashJSON(file: File): Promise<JsonImportResult> {
  const result: JsonImportResult = {
    success: false,
    accounts: 0,
    transactions: 0,
    categories: 0,
    budgets: 0,
    errors: [],
  };

  try {
    const text = await file.text();
    const data = JSON.parse(text);

    log('Starting JSON import', {
      accounts: data.accounts?.length || 0,
      transactions: data.transactions?.length || 0,
      categories: data.categories?.length || 0,
      budgets: data.budgets?.length || 0,
    });

    // Validate structure
    if (!data.accounts && !data.transactions && !data.categories) {
      result.errors.push('Invalid backup file: missing accounts, transactions, or categories');
      return result;
    }

    // Import categories first (transactions depend on them)
    // Note: Categories don't have syncStatus field
    if (data.categories && Array.isArray(data.categories)) {
      for (const cat of data.categories) {
        try {
          // Convert date strings back to Date objects
          const category = {
            ...cat,
            createdAt: cat.createdAt ? new Date(cat.createdAt) : new Date(),
            updatedAt: cat.updatedAt ? new Date(cat.updatedAt) : new Date(),
          };
          await db.categories.put(category);
          result.categories++;
        } catch (err) {
          logWarn('Failed to import category', { id: cat.id, error: err });
        }
      }
    }

    // Import accounts
    // BUG-044 FIX: Set syncStatus: 'pending' so imported data gets pushed to server
    if (data.accounts && Array.isArray(data.accounts)) {
      for (const acc of data.accounts) {
        try {
          const account = {
            ...acc,
            createdAt: acc.createdAt ? new Date(acc.createdAt) : new Date(),
            updatedAt: acc.updatedAt ? new Date(acc.updatedAt) : new Date(),
            syncStatus: 'pending' as const, // BUG-044 FIX: Mark for sync
          };
          await db.accounts.put(account);
          result.accounts++;
        } catch (err) {
          logWarn('Failed to import account', { id: acc.id, error: err });
        }
      }
    }

    // Import transactions
    // BUG-044 FIX: Set syncStatus: 'pending' so imported data gets pushed to server
    if (data.transactions && Array.isArray(data.transactions)) {
      for (const tx of data.transactions) {
        try {
          const transaction = {
            ...tx,
            date: tx.date ? new Date(tx.date) : new Date(),
            createdAt: tx.createdAt ? new Date(tx.createdAt) : new Date(),
            updatedAt: tx.updatedAt ? new Date(tx.updatedAt) : new Date(),
            syncStatus: 'pending' as const, // BUG-044 FIX: Mark for sync
          };
          await db.transactions.put(transaction);
          result.transactions++;
        } catch (err) {
          logWarn('Failed to import transaction', { id: tx.id, error: err });
        }
      }
    }

    // Import budgets
    if (data.budgets && Array.isArray(data.budgets)) {
      for (const budget of data.budgets) {
        try {
          const b = {
            ...budget,
            createdAt: budget.createdAt ? new Date(budget.createdAt) : new Date(),
            updatedAt: budget.updatedAt ? new Date(budget.updatedAt) : new Date(),
          };
          await db.budgets.put(b);
          result.budgets++;
        } catch (err) {
          logWarn('Failed to import budget', { id: budget.id, error: err });
        }
      }
    }

    // Import holdings adjustments (crypto gains/losses/value syncs)
    // Marked pending so restored adjustments push to the server (BUG-044 pattern)
    if (data.holdingsAdjustments && Array.isArray(data.holdingsAdjustments)) {
      let restoredAdjustments = 0;
      for (const adj of data.holdingsAdjustments) {
        try {
          await db.holdingsAdjustments.put({
            ...adj,
            date: adj.date ? new Date(adj.date) : new Date(),
            deletedAt: adj.deletedAt ? new Date(adj.deletedAt) : undefined,
            createdAt: adj.createdAt ? new Date(adj.createdAt) : new Date(),
            updatedAt: adj.updatedAt ? new Date(adj.updatedAt) : new Date(),
            syncStatus: 'pending' as const,
          });
          restoredAdjustments++;
        } catch (err) {
          logWarn('Failed to import holdings adjustment', { id: adj.id, error: err });
        }
      }
      if (restoredAdjustments > 0) {
        log(`Restored ${restoredAdjustments} holdings adjustment(s)`);
      }
    }

    result.success = true;
    log('JSON import complete', result);

    // BUG-044 FIX: Emit events and trigger sync push after import
    // This ensures imported data gets pushed to server for cross-device sync
    dataEvents.emit('all');
    syncManager.triggerPush();
    log('Triggered sync push after JSON import');

  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    result.errors.push(`Failed to parse JSON: ${msg}`);
    logError('JSON import failed', { error: msg });
  }

  return result;
}

// ============================================================
// Export for testing
// ============================================================

export const _internal = {
  isErrorFixTransaction,
  normalizeAccountName,
  parseDate,
  separateFooterBalances,
  RECURRING_PATTERNS,
  ACCOUNT_NAME_MAP,
};
